/**
 * CV Analysis page component for ClarCV application.
 *
 * Displays comprehensive CV analysis results including scores, recommendations,
 * strengths, weaknesses, and detailed feedback from AI analysis.
 *
 * @component
 * @returns {JSX.Element} CV analysis page component
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import AnalyzingProgress from '../components/AnalyzingProgress';
import AnalysisHelpForm from '../components/AnalysisHelpForm';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useRequireAuth } from '../contexts/AuthGateContext';
import Seo from '../components/Seo';
import { usePlanLimitPrompt } from '../hooks/usePlanLimitPrompt';
import { trackAtsAnalysisOk, trackCvExtractOk, trackCvUpload, trackGaEvent } from '../utils/analytics';
import { startStripeCheckout } from '../utils/checkout';
import { rememberAiConsent } from '../utils/aiConsent';
import { requireCookieChoice } from '../utils/cookieConsent';
import { recalledJobPosition, rememberJobPosition } from '../utils/jobPosition';
import { getErrorPayload } from '../utils/planLimit';
import { rewriteCvFromStoredFile } from '../utils/startCvRewrite';
import './FlowPages.css';

const DETAILED_FEEDBACK_LABELS = {
  contact: 'Datos de contacto',
  contact_info: 'Datos de contacto',
  summary: 'Resumen',
  professional_summary: 'Resumen profesional',
  experience: 'Experiencia',
  work_experience: 'Experiencia laboral',
  education: 'Formación',
  skills: 'Habilidades',
  industry_fit: 'Encaje con el sector',
  recommendations: 'Recomendaciones',
  critical_issues: 'Aspectos críticos',
};

const SECTION_SCORE_ROWS = [
  { key: 'contact_score', labelKey: 'flow.teaserSectionContact' },
  { key: 'summary_score', labelKey: 'flow.teaserSectionSummary' },
  { key: 'experience_score', labelKey: 'flow.teaserSectionExperience' },
  { key: 'education_score', labelKey: 'flow.teaserSectionEducation' },
  { key: 'skills_score', labelKey: 'flow.teaserSectionSkills' },
];

const SCORE_BAND_COLORS = {
  excellent: '#15803d',
  solid: '#1d4ed8',
  fair: '#b45309',
  weak: '#b91c1c',
};

const TEASER_PLAN_SIZE = 10;

const TEASER_LOCKED_TITLE_KEYS = [
  'flow.teaserPeekTitle2',
  'flow.teaserPeekTitle3',
  'flow.teaserPeekTitle4',
  'flow.teaserPeekTitle5',
  'flow.teaserPeekTitle6',
  'flow.teaserPeekTitle7',
  'flow.teaserPeekTitle8',
  'flow.teaserPeekTitle9',
  'flow.teaserPeekTitle10',
];

/**
 * Build the teaser action-plan list: one real lead title plus locked rows
 * so the preview reads as a ~10-step plan.
 *
 * @param {Object} analysis - Teaser payload from the API
 * @param {Function} t - Translator
 * @returns {{ leadTitle: string, lockedTitles: string[] }}
 */
const buildTeaserPlanTitles = (analysis, t) => {
  const fromApi = Array.isArray(analysis.peek_titles)
    ? analysis.peek_titles.filter((title) => typeof title === 'string' && title.trim())
    : [];
  const leadTitle = (
    fromApi[0]
    || analysis.next_action
    || t('flow.teaserPeekTitle1')
  ).trim();
  const used = new Set([leadTitle.toLowerCase()]);
  const lockedTitles = [];

  const tryPush = (title) => {
    const trimmed = (title || '').trim();
    if (
      !trimmed
      || used.has(trimmed.toLowerCase())
      || lockedTitles.length >= TEASER_PLAN_SIZE - 1
    ) {
      return;
    }
    used.add(trimmed.toLowerCase());
    lockedTitles.push(trimmed);
  };

  fromApi.slice(1).forEach(tryPush);
  TEASER_LOCKED_TITLE_KEYS.forEach((key) => tryPush(t(key)));

  return { leadTitle, lockedTitles };
};

/**
 * Scroll the window to the top, respecting reduced-motion preferences.
 */
const scrollWindowToTop = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
};

/**
 * Parse a numeric score, or null if missing.
 *
 * @param {*} value - Raw score from the API
 * @returns {number|null} Finite number or null
 */
const asScore = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Clamp a score into 0–100 for bars and rings.
 *
 * @param {number} value - Score value
 * @returns {number} Clamped score
 */
const clampScore = (value) => Math.max(0, Math.min(100, Number(value) || 0));

/**
 * Band id used for color and copy (excellent / solid / fair / weak).
 *
 * @param {number} score - Score 0–100
 * @returns {string} Band key
 */
const getScoreBand = (score) => {
  const n = clampScore(score);
  if (n >= 80) return 'excellent';
  if (n >= 65) return 'solid';
  if (n >= 45) return 'fair';
  return 'weak';
};

/**
 * Color for a score band.
 *
 * @param {number} score - Score 0–100
 * @returns {string} Hex color
 */
const getScoreColor = (score) => SCORE_BAND_COLORS[getScoreBand(score)];

/**
 * Circular score metric used in the report hero.
 *
 * @param {object} props - Component props
 * @returns {JSX.Element|null} Metric card
 */
const ScoreRing = ({ value, suffix, label, hint, t }) => {
  if (value === null) {
    return null;
  }
  const pct = clampScore(value);
  const band = getScoreBand(pct);
  const color = SCORE_BAND_COLORS[band];
  return (
    <div className="ats-report__metric">
      <div
        className="ats-report__ring"
        style={{
          '--ring-color': color,
          '--ring-pct': pct,
        }}
        role="img"
        aria-label={`${label}: ${pct}${suffix}`}
      >
        <span className="ats-report__ring-inner">
          <span>{pct}</span>
          <small>{suffix}</small>
        </span>
      </div>
      <p className="ats-teaser__kicker">{label}</p>
      <p className={`ats-report__band ats-report__band--${band}`}>
        {t(`flow.scoreBand_${band}`)}
      </p>
      {hint ? <p className="ats-teaser__metric-hint">{hint}</p> : null}
    </div>
  );
};

ScoreRing.propTypes = {
  value: PropTypes.number,
  suffix: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  t: PropTypes.func.isRequired,
};

ScoreRing.defaultProps = {
  value: null,
  hint: '',
};

/**
 * Horizontal section scores (contact, summary, experience, …).
 *
 * @param {object} props - Component props
 * @returns {JSX.Element|null} Section list
 */
const SectionScoreList = ({ analysis, t, showBand = false }) => {
  const rows = SECTION_SCORE_ROWS
    .map((row) => ({ ...row, value: asScore(analysis[row.key]) }))
    .filter((row) => row.value !== null);
  if (!rows.length) {
    return null;
  }
  return (
    <section className="ats-teaser__card">
      <h2 className="ats-teaser__h">{t('flow.teaserSections')}</h2>
      <ul className="ats-teaser__bars">
        {rows.map((row) => {
          const value = clampScore(row.value);
          const band = getScoreBand(value);
          return (
            <li key={row.key}>
              <div className="ats-teaser__bar-meta">
                <span>{t(row.labelKey)}</span>
                <span className="ats-report__bar-value">
                  {showBand ? (
                    <em className={`ats-report__band ats-report__band--${band}`}>
                      {t(`flow.scoreBand_${band}`)}
                    </em>
                  ) : null}
                  <strong>{value}</strong>
                </span>
              </div>
              <div className="ats-teaser__track" aria-hidden="true">
                <div
                  className="ats-teaser__fill"
                  style={{
                    width: `${value}%`,
                    background: getScoreColor(value),
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

SectionScoreList.propTypes = {
  analysis: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  showBand: PropTypes.bool,
};

SectionScoreList.defaultProps = {
  showBand: false,
};

const CVAnalysis = () => {
  const { cvId } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, isPro } = useAuth();
  const requireAuth = useRequireAuth();
  const [cv, setCv] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [jobPosition, setJobPosition] = useState('');
  const [aiConsent, setAiConsent] = useState(false);
  const scrollToResultsOnComplete = useRef(false);
  const { handlePlanLimitError, prompt: upgradePrompt } = usePlanLimitPrompt({
    rewriteCvId: cv?.id || cvId,
    rewriteJob: jobPosition,
  });

  /**
   * Open Stripe Checkout for the 0,99 € ATS report. The gold teaser
   * button already states the price, so skip the UpgradePrompt modal.
   */
  const startAtsCheckout = async () => {
    trackGaEvent('upgrade_prompt_click', {
      reason: 'ats_teaser',
      kind: 'oneshot_ats',
    });
    if (isPro) {
      return;
    }
    if (!isAuthenticated) {
      navigate('/pricing');
      return;
    }
    setCheckoutLoading(true);
    try {
      await startStripeCheckout({
        kind: 'oneshot_ats',
        returnPath: window.location.pathname,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || t('pricing.checkoutError'));
    } finally {
      setCheckoutLoading(false);
    }
  };

  /**
   * Rewrite the same ATS file with AI and open the studio — no second upload screen.
   */
  const handleStartRewrite = async () => {
    const sourceId = cv?.id || cvId;
    if (!sourceId) {
      return;
    }
    if (!(await requireAuth())) {
      return;
    }
    await requireCookieChoice();
    setRewriting(true);
    try {
      rememberAiConsent();
      rememberJobPosition(String(sourceId), jobPosition);
      const response = await rewriteCvFromStoredFile({
        sourceCvId: sourceId,
        jobPosition,
      });
      trackCvUpload({ source: 'ats_rewrite_inplace' });
      trackCvExtractOk();
      toast.success(t('flow.analyzeOk'));
      navigate('/dashboard/edit-cv', {
        state: { analysisData: response.data },
        replace: true,
      });
    } catch (error) {
      if (await handlePlanLimitError(error)) {
        return;
      }
      const errorData = (await getErrorPayload(error)) || error.response?.data || {};
      const errorMessage =
        errorData.error_code === 'ai_consent_required'
          ? t('flow.errNeedConsent')
          : errorData.error_code === 'pdf_no_text' ||
              /extract text|parsing PDF|no readable text|image-only|redacted/i.test(
                String(errorData.error || '')
              )
            ? t('flow.errPdfNoText')
            : errorData.error === 'No file provided'
              ? t('flow.errNeedFile')
              : errorData.error || errorData.detail || t('flow.analyzeFail');
      toast.error(errorMessage);
    } finally {
      setRewriting(false);
    }
  };

  const analyzeSteps = [
    t('flow.analyzeStep1'),
    t('flow.analyzeStep2'),
    t('flow.analyzeStep3'),
    t('flow.analyzeStep4'),
  ];

  useEffect(() => {
    const fetchCV = async () => {
      try {
        const response = await axios.get(`/api/cvs/${cvId}/`);
        setCv(response.data);

        if (response.data.analysis) {
          setAnalysis(response.data.analysis);
          const storedJob = (
            response.data.analysis.job_position
            || recalledJobPosition(cvId)
            || ''
          ).trim();
          if (storedJob) {
            setJobPosition(storedJob);
          }
        }
      } catch (error) {
        console.error('Error fetching CV:', error);
        toast.error('Error al cargar el CV');
      } finally {
        setLoading(false);
      }
    };

    fetchCV();
  }, [cvId]);

  useEffect(() => {
    if (analysis) {
      rememberAiConsent();
    }
    if (!analysis || !scrollToResultsOnComplete.current) {
      return;
    }
    scrollToResultsOnComplete.current = false;
    scrollWindowToTop();
  }, [analysis]);

  /**
   * Start CV analysis with selected industry and job position.
   */
  const startAnalysis = async () => {
    if (!selectedIndustry.trim()) {
      toast.error('Indica un sector o industria');
      return;
    }
    if (!aiConsent) {
      toast.error(t('flow.errNeedConsent'));
      return;
    }

    await requireCookieChoice();

    setAnalyzing(true);

    try {
      rememberAiConsent();
      rememberJobPosition(cvId, jobPosition);
      const analysisData = {
        cv_id: parseInt(cvId, 10),
        industry: selectedIndustry,
        ai_consent: true,
      };

      if (jobPosition.trim()) {
        analysisData.job_position = jobPosition.trim();
      }

      const response = await axios.post('/api/cvs/analyze/', analysisData);

      const analyzingToastId = toast.info(t('flow.analyzingLive'), {
        autoClose: false,
        isLoading: true,
      });
      pollAnalysisStatus(response.data.task_id, analyzingToastId);
    } catch (error) {
      console.error('Error starting analysis:', error);
      if (await handlePlanLimitError(error)) {
        setAnalyzing(false);
        return;
      }
      const msg = error.response?.data?.error || 'Error al iniciar el análisis';
      toast.error(msg);
      setAnalyzing(false);
    }
  };

  /**
   * Poll analysis status until completion.
   *
   * @param {string} taskId - Celery task ID for the analysis
   * @param {string|number} [analyzingToastId] - Toast to update when done
   */
  const pollAnalysisStatus = async (taskId, analyzingToastId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/cvs/analysis/status/${taskId}/`);

        if (response.data.status === 'SUCCESS') {
          scrollToResultsOnComplete.current = true;
          setAnalysis(response.data.analysis);
          setAnalyzing(false);
          clearInterval(pollInterval);
          trackAtsAnalysisOk();
          if (analyzingToastId != null) {
            toast.update(analyzingToastId, {
              render: 'Análisis completado',
              type: 'success',
              autoClose: 3000,
              isLoading: false,
            });
          } else {
            toast.success('Análisis completado');
          }
        } else if (response.data.status === 'FAILURE') {
          setAnalyzing(false);
          clearInterval(pollInterval);
          if (analyzingToastId != null) {
            toast.update(analyzingToastId, {
              render: 'Error en el análisis',
              type: 'error',
              autoClose: 5000,
              isLoading: false,
            });
          } else {
            toast.error('Error en el análisis');
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
        setAnalyzing(false);
        clearInterval(pollInterval);
      }
    }, 2000);
  };

  if (loading) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        {t('flow.loadingCv')}
      </div>
    );
  }

  if (!cv) {
    return (
      <div className="container flow-page">
        <div className="flow-card">
          <p className="flow-lead">{t('flow.cvNotFound')}</p>
          <Link to="/dashboard" className="btn btn-primary">
            {t('flow.backToDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  const canStart = Boolean(selectedIndustry.trim() && aiConsent && !analyzing);

  if (!analysis) {
    return (
      <div className="container flow-page">
        <Seo
          title={t('flow.analyzeTitle')}
          description={t('flow.analyzeLead')}
          path={`/dashboard/cv/${cvId}`}
          noindex
        />
        <div className="flow-card">
          <Link to="/dashboard/from-file" className="flow-back">
            ← {t('flow.backFromFile')}
          </Link>
          <p className="flow-step">{t('flow.stepOf', { current: 2, total: 2 })}</p>
          <h1 className="flow-title">{t('flow.analyzeTitle')}</h1>
          <p className="flow-lead">{t('flow.analyzeLead')}</p>
          <AnalyzingProgress
            active={analyzing}
            title={t('flow.analyzeProgressTitle')}
            steps={analyzeSteps}
          />
          {cv.title ? (
            <p className="flow-file-meta">{t('flow.analyzeFile', { name: cv.title })}</p>
          ) : null}

          <div className="form-group">
            <label htmlFor="cv-analysis-industry">{t('flow.industryLabel')}</label>
            <input
              id="cv-analysis-industry"
              type="text"
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              placeholder={t('flow.industryPlaceholder')}
              autoComplete="organization-title"
              required
            />
            <p className="flow-field__hint">{t('flow.industryHint')}</p>
          </div>

          <div className="form-group">
            <label htmlFor="cv-analysis-job">
              {t('flow.jobLabel')}{' '}
              <span className="flow-field__optional">{t('flow.jobOptional')}</span>
            </label>
            <input
              id="cv-analysis-job"
              type="text"
              value={jobPosition}
              onChange={(e) => setJobPosition(e.target.value)}
              placeholder={t('flow.jobPlaceholder')}
            />
            <p className="flow-field__hint">{t('flow.jobHint')}</p>
          </div>

          <label className="flow-consent" htmlFor="cv-analysis-ai-consent">
            <input
              id="cv-analysis-ai-consent"
              type="checkbox"
              checked={aiConsent}
              onChange={(e) => setAiConsent(e.target.checked)}
            />
            <span>{t('flow.aiConsent')}</span>
          </label>

          <div className="flow-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={startAnalysis}
              disabled={!canStart}
              aria-busy={analyzing}
            >
              {analyzing ? t('flow.analyzing') : t('flow.startAnalysis')}
            </button>
          </div>

          <div className="flow-next">
            <h2 className="flow-next__title">{t('flow.analyzeIncludesTitle')}</h2>
            <ul>
              <li>{t('flow.analyzeInclude1')}</li>
              <li>{t('flow.analyzeInclude2')}</li>
              <li>{t('flow.analyzeInclude3')}</li>
              <li>{t('flow.analyzeInclude4')}</li>
            </ul>
          </div>
        </div>
        {upgradePrompt}
      </div>
    );
  }

  if (rewriting) {
    return (
      <div className="container flow-page">
        <Seo
          title={t('flow.rewriteFromAtsTitle')}
          description={t('flow.rewriteFromAtsLead')}
          path={`/dashboard/cv/${cvId}`}
          noindex
        />
        <div className="flow-card">
          <h1 className="flow-title">{t('flow.rewriteFromAtsTitle')}</h1>
          <p className="flow-lead">{t('flow.rewriteFromAtsLead')}</p>
          {cv.title ? (
            <p className="flow-file-meta">{t('flow.analyzeFile', { name: cv.title })}</p>
          ) : null}
          <AnalyzingProgress
            active
            title={t('flow.generateProgressTitle')}
            steps={[
              t('flow.generateStep1'),
              t('flow.generateStep2'),
              t('flow.generateStep3'),
              t('flow.analyzeStep4'),
            ]}
          />
        </div>
        {upgradePrompt}
      </div>
    );
  }

  const gaps = Array.isArray(analysis.weaknesses) ? analysis.weaknesses : [];
  const strengths = Array.isArray(analysis.strengths) ? analysis.strengths : [];
  const atsScore = asScore(analysis.ats_compatibility);
  const overall = asScore(analysis.overall_score);
  const keywords = asScore(analysis.keyword_density);
  const overallBand = overall !== null ? getScoreBand(overall) : 'fair';
  const jobLabel = (jobPosition || analysis.job_position || '').trim();

  if (analysis.is_teaser) {
    const { leadTitle, lockedTitles } = buildTeaserPlanTitles(analysis, t);
    const lockedCount = lockedTitles.length;
    const peekLead =
      analysis.peek_lead && typeof analysis.peek_lead === 'object'
        ? analysis.peek_lead
        : {};
    const leadWhy = typeof peekLead.why === 'string' ? peekLead.why.trim() : '';
    const leadHow = typeof peekLead.how === 'string' ? peekLead.how.trim() : '';
    const leadExample =
      typeof peekLead.example === 'string' ? peekLead.example.trim() : '';
    const hasCvAdvice = Boolean(leadWhy || leadHow || leadExample);

    return (
      <div className="container flow-results">
        <Seo
          title={cv.title || t('flow.analyzeTitle')}
          description={t('flow.teaserLead')}
          path={`/dashboard/cv/${cvId}`}
          noindex
        />
        <Link to="/dashboard/from-file" className="flow-back">
          ← {t('flow.backFromFile')}
        </Link>
        <header className="flow-results__header">
          <p className="ats-report__badge">{t('flow.reportPreviewBadge')}</p>
          <h1 className="flow-title">{cv.title}</h1>
          <p className="flow-lead">{t('flow.teaserLead')}</p>
          {jobLabel ? (
            <p className="ats-report__job">
              {t('flow.reportForJob', { job: jobLabel })}
            </p>
          ) : null}
        </header>

        <section className="ats-report__hero" aria-label={t('flow.teaserScoresLabel')}>
          <ScoreRing
            value={overall}
            suffix="/100"
            label={t('flow.teaserScore')}
            hint={t('flow.teaserScoreHint')}
            t={t}
          />
          <ScoreRing
            value={atsScore}
            suffix="%"
            label={t('flow.teaserAts')}
            hint={t('flow.teaserAtsHint')}
            t={t}
          />
        </section>

        {overall !== null ? (
          <p className={`ats-report__verdict ats-report__verdict--${overallBand}`}>
            {t(`flow.scoreVerdict_${overallBand}`)}
          </p>
        ) : null}

        <AnalysisHelpForm
          hero
          jobPosition={jobPosition}
          cvId={cv?.id || cvId}
          fileName={cv?.title || ''}
          onStartRewrite={handleStartRewrite}
          rewriting={rewriting}
        />

        <div className="ats-teaser__split">
          {strengths.length > 0 ? (
            <section className="ats-teaser__card ats-teaser__card--ok">
              <h2 className="ats-teaser__h">{t('flow.teaserStrengths')}</h2>
              <ul>
                {strengths.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          ) : null}
          {gaps.length > 0 ? (
            <section className="ats-teaser__card ats-teaser__card--gap">
              <h2 className="ats-teaser__h">{t('flow.teaserGaps')}</h2>
              <ul>
                {gaps.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        <SectionScoreList analysis={analysis} t={t} />

        {analysis.teaser_summary ? (
          <section className="ats-teaser__card">
            <h2 className="ats-teaser__h">{t('flow.teaserBlurb')}</h2>
            <p className="ats-teaser__blurb">{analysis.teaser_summary}</p>
          </section>
        ) : null}

        <section className="ats-teaser__peek" aria-label={t('flow.teaserPeekPlan')}>
          <header className="ats-teaser__peek-head">
            <div>
              <h2 className="ats-teaser__h">{t('flow.teaserPeekPlan')}</h2>
              <p className="ats-teaser__peek-hint">
                {t('flow.teaserPeekHint', { rest: lockedCount })}
              </p>
            </div>
            <p className="ats-teaser__peek-count">
              {t('flow.teaserPeekCount', { n: TEASER_PLAN_SIZE })}
            </p>
          </header>

          <article className="ats-report__action is-lead ats-teaser__sample">
            <div className="ats-report__action-head">
              <span className="ats-report__action-num" aria-hidden="true">
                1
              </span>
              <div>
                <p className="ats-report__action-kicker">{t('flow.reportActionLead')}</p>
                <h3 className="ats-report__action-title">{leadTitle}</h3>
              </div>
            </div>
            {hasCvAdvice ? (
              <>
                {leadWhy ? (
                  <p className="ats-report__action-p">
                    <span>{t('flow.reportActionWhy')}</span>
                    {leadWhy}
                  </p>
                ) : null}
                {leadHow ? (
                  <p className="ats-report__action-p">
                    <span>{t('flow.reportActionHow')}</span>
                    {leadHow}
                  </p>
                ) : null}
                {leadExample ? (
                  <blockquote className="ats-report__example">
                    <span>{t('flow.reportActionExample')}</span>
                    {leadExample}
                  </blockquote>
                ) : null}
              </>
            ) : (
              <>
                <p className="ats-report__action-p">
                  <span>{t('flow.reportActionWhy')}</span>
                  {t('flow.teaserPeekWhyDummy')}
                </p>
                <p className="ats-report__action-p">
                  <span>{t('flow.reportActionHow')}</span>
                  {t('flow.teaserPeekHowDummy')}
                </p>
                <blockquote className="ats-report__example">
                  <span>{t('flow.reportActionExample')}</span>
                  <dl className="ats-teaser__example-pair">
                    <div className="ats-teaser__example-before">
                      <dt>{t('flow.teaserPeekBefore')}</dt>
                      <dd>{t('flow.teaserPeekExampleBefore')}</dd>
                    </div>
                    <div className="ats-teaser__example-after">
                      <dt>{t('flow.teaserPeekAfter')}</dt>
                      <dd>{t('flow.teaserPeekExampleAfter')}</dd>
                    </div>
                  </dl>
                </blockquote>
              </>
            )}
          </article>

          {lockedTitles.length > 0 ? (
            <div className="ats-teaser__peek-rest" aria-hidden="true">
              {lockedTitles.map((title, index) => (
                <article key={`${title}-${index}`} className="ats-teaser__ghost-row">
                  <span className="ats-teaser__ghost-num">{index + 2}</span>
                  <div>
                    <p className="ats-teaser__ghost-title">{title}</p>
                    <p className="ats-teaser__ghost-line">{t('flow.teaserPeekLockedLine')}</p>
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </section>

        <section className="ats-teaser__pro">
          <p className="ats-teaser__pro-badge">{t('flow.teaserProBadge')}</p>
          <h2 className="ats-teaser__pro-title">{t('flow.teaserLockedTitle')}</h2>
          <p className="ats-teaser__pro-lead">{t('flow.teaserLockedLead')}</p>
          <ul className="ats-teaser__unlocks">
            <li>{t('flow.teaserUnlock1')}</li>
            <li>{t('flow.teaserUnlock2')}</li>
            <li>{t('flow.teaserUnlock3')}</li>
            <li>{t('flow.teaserUnlock4')}</li>
          </ul>
          <div className="ats-teaser__pro-cta-wrap">
            <p className="ats-teaser__price">
              <span className="ats-teaser__price-value">{t('flow.teaserPrice')}</span>
              <span className="ats-teaser__price-period">{t('flow.teaserPricePeriod')}</span>
            </p>
            <button
              type="button"
              className="btn btn-primary ats-teaser__cta"
              onClick={startAtsCheckout}
              disabled={checkoutLoading}
            >
              {checkoutLoading ? t('pricing.upgrading') : t('flow.teaserCta')}
            </button>
            <p className="ats-teaser__cta-hint">{t('flow.teaserCtaHint')}</p>
          </div>
        </section>
        {upgradePrompt}
      </div>
    );
  }

  const actionPlan = Array.isArray(analysis.action_plan) ? analysis.action_plan : [];
  const suggestions = Array.isArray(analysis.suggestions) ? analysis.suggestions : [];
  const detailEntries = analysis.detailed_feedback
    ? Object.entries(analysis.detailed_feedback).filter(([, value]) => Boolean(value))
    : [];

  return (
    <div className="container flow-results">
      <Seo
        title={cv.title || t('flow.analyzeTitle')}
        description={t('flow.analysisSubtitle')}
        path={`/dashboard/cv/${cvId}`}
        noindex
      />
      <Link to="/dashboard/from-file" className="flow-back">
        ← {t('flow.backFromFile')}
      </Link>
      <header className="flow-results__header">
        <p className="ats-report__badge ats-report__badge--pro">{t('flow.reportProBadge')}</p>
        <h1 className="flow-title">{cv.title}</h1>
        <p className="flow-lead">{t('flow.analysisSubtitle')}</p>
        {jobLabel ? (
          <p className="ats-report__job">
            {t('flow.reportForJob', { job: jobLabel })}
          </p>
        ) : null}
      </header>

      <section className={`ats-report__hero${keywords !== null ? ' ats-report__hero--pro' : ''}`} aria-label={t('flow.reportOverall')}>
        <ScoreRing
          value={overall}
          suffix="/100"
          label={t('flow.teaserScore')}
          hint={t('flow.teaserScoreHint')}
          t={t}
        />
        <ScoreRing
          value={atsScore}
          suffix="%"
          label={t('flow.teaserAts')}
          hint={t('flow.teaserAtsHint')}
          t={t}
        />
        <ScoreRing
          value={keywords}
          suffix="%"
          label={t('flow.teaserKeywords')}
          hint={t('flow.teaserKeywordsHint')}
          t={t}
        />
      </section>

      {overall !== null ? (
        <p className={`ats-report__verdict ats-report__verdict--${overallBand}`}>
          {t(`flow.scoreVerdict_${overallBand}`)}
        </p>
      ) : null}

      {(analysis.executive_summary || analysis.industry_fit) && (
        <section className="ats-teaser__card ats-report__brief">
          <p className="ats-teaser__kicker">{t('flow.reportSummary')}</p>
          {analysis.executive_summary ? (
            <p className="ats-teaser__blurb">{analysis.executive_summary}</p>
          ) : null}
          {analysis.industry_fit ? (
            <div className="ats-report__fit">
              <h2 className="ats-teaser__h">{t('flow.reportFit')}</h2>
              <p className="ats-teaser__blurb">{analysis.industry_fit}</p>
            </div>
          ) : null}
        </section>
      )}

      <section className="ats-teaser__card ats-report__plan">
        <p className="ats-teaser__kicker">{t('flow.teaserPeekPlan')}</p>
        <h2 className="ats-teaser__h">{t('flow.reportActionTitle')}</h2>
        <p className="ats-report__plan-hint">{t('flow.reportActionHint')}</p>
        {actionPlan.length > 0 ? (
          <ol className="ats-report__actions">
            {actionPlan.map((item, index) => {
              const title = typeof item === 'string' ? item : (item.title || `Acción ${index + 1}`);
              const why = typeof item === 'object' ? item.why : '';
              const how = typeof item === 'object' ? item.how : '';
              const example = typeof item === 'object' ? item.example : '';
              const priority = typeof item === 'object' ? (item.priority || index + 1) : index + 1;
              const isLead = index === 0;
              return (
                <li
                  key={`${title}-${index}`}
                  className={`ats-report__action${isLead ? ' is-lead' : ''}`}
                >
                  <div className="ats-report__action-head">
                    <span className="ats-report__action-num" aria-hidden="true">
                      {priority}
                    </span>
                    <div>
                      {isLead ? (
                        <p className="ats-report__action-kicker">{t('flow.reportActionLead')}</p>
                      ) : (
                        <p className="ats-report__action-kicker">
                          {t('flow.teaserPeekPriority', { n: priority })}
                        </p>
                      )}
                      <h3 className="ats-report__action-title">{title}</h3>
                    </div>
                  </div>
                  {why ? (
                    <p className="ats-report__action-p">
                      <span>{t('flow.reportActionWhy')}</span>
                      {why}
                    </p>
                  ) : null}
                  {how ? (
                    <p className="ats-report__action-p">
                      <span>{t('flow.reportActionHow')}</span>
                      {how}
                    </p>
                  ) : null}
                  {example ? (
                    <blockquote className="ats-report__example">
                      <span>{t('flow.reportActionExample')}</span>
                      {example}
                    </blockquote>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : (
          <div className="ats-report__suggestions">
            {suggestions.map((suggestion, index) => (
              <p key={index} className="ats-report__action-p">
                {typeof suggestion === 'string'
                  ? suggestion
                  : suggestion.title || JSON.stringify(suggestion)}
              </p>
            ))}
          </div>
        )}
      </section>

      <AnalysisHelpForm
        jobPosition={jobPosition}
        cvId={cv?.id || cvId}
        fileName={cv?.title || ''}
        onStartRewrite={handleStartRewrite}
        rewriting={rewriting}
      />

      <div className="ats-teaser__split">
        {strengths.length > 0 ? (
          <section className="ats-teaser__card ats-teaser__card--ok">
            <h2 className="ats-teaser__h">{t('flow.teaserStrengths')}</h2>
            <ul>
              {strengths.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        ) : null}
        {gaps.length > 0 ? (
          <section className="ats-teaser__card ats-teaser__card--gap">
            <h2 className="ats-teaser__h">{t('flow.teaserGaps')}</h2>
            <ul>
              {gaps.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      <SectionScoreList analysis={analysis} t={t} showBand />

      {detailEntries.length > 0 ? (
        <section className="ats-teaser__card">
          <p className="ats-teaser__kicker">{t('flow.reportDetailsKicker')}</p>
          <h2 className="ats-teaser__h">{t('flow.reportDetails')}</h2>
          <div className="ats-report__details">
            {detailEntries.map(([key, value]) => (
              <article key={key} className="ats-report__detail">
                <h3>{DETAILED_FEEDBACK_LABELS[key] || key.replace(/_/g, ' ')}</h3>
                <p>{value}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {upgradePrompt}
    </div>
  );
};

export default CVAnalysis;
